import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const IssueDetail = () => {
  const { id } = useParams();
  const [issue, setIssue] = useState(null);
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchIssueDetails();
      fetchComments();
      fetchActivities();
      fetchUsers();
    }
  }, [id, isAuthenticated, authLoading]);

  const fetchIssueDetails = async () => {
    try {
      console.log('Fetching issue details for:', id);
      const response = await axios.get(`/issues/${id}`);
      setIssue(response.data);
      console.log('Issue details loaded:', response.data.title);
    } catch (error) {
      setError('Failed to load issue details');
      console.error('Error loading issue details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`/issues/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments');
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`/issues/${id}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Failed to load activities');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      // Users endpoint might be admin-only
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await axios.put(`/issues/${id}`, {
        status: newStatus
      });
      setIssue(response.data);
      fetchActivities(); // Refresh activities
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleAssignment = async (assigneeId) => {
    try {
      const response = await axios.put(`/issues/${id}`, {
        assigned_to: assigneeId || null
      });
      setIssue(response.data);
      fetchActivities(); // Refresh activities
    } catch (error) {
      alert('Failed to update assignment');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await axios.post(`/issues/${id}/comments`, {
        content: newComment
      });
      setNewComment('');
      fetchComments();
      fetchActivities();
    } catch (error) {
      alert('Failed to add comment');
    }
  };

  const handleDeleteIssue = async () => {
    if (!window.confirm('Are you sure you want to delete this issue?')) {
      return;
    }

    try {
      await axios.delete(`/issues/${id}`);
      navigate('/issues');
    } catch (error) {
      alert('Failed to delete issue');
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'Open': 'badge-open',
      'In Progress': 'badge-progress',
      'Closed': 'badge-closed',
      'Resolved': 'badge-closed'
    };
    return statusClasses[status] || 'badge-open';
  };

  const getPriorityBadgeClass = (priority) => {
    const priorityClasses = {
      'Low': 'badge-low',
      'Medium': 'badge-medium',
      'High': 'badge-high',
      'Critical': 'badge-critical'
    };
    return priorityClasses[priority] || 'badge-medium';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div className="main-content">
        <div className="alert alert-danger">
          {error || 'Issue not found'}
        </div>
        <Link to="/issues" className="btn btn-outline">
          Back to Issues
        </Link>
      </div>
    );
  }

  const canEdit = user?.role === 'Admin' || issue.creator_id === user?.id;

  return (
    <div className="main-content">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-3xl font-bold">Issue #{issue.id}</h1>
            <span className={`badge ${getStatusBadgeClass(issue.status)}`}>
              {issue.status}
            </span>
            <span className={`badge ${getPriorityBadgeClass(issue.priority)}`}>
              {issue.priority}
            </span>
          </div>
          <h2 className="text-xl text-gray-700 mb-2">{issue.title}</h2>
          <div className="text-sm text-gray-600">
            Created by {issue.creator_name} on {formatDate(issue.created_at)}
            {issue.updated_at !== issue.created_at && (
              <span> • Updated {formatDate(issue.updated_at)}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <>
              <Link 
                to={`/issues/${id}/edit`}
                className="btn btn-secondary btn-sm"
              >
                Edit Issue
              </Link>
              <button
                onClick={handleDeleteIssue}
                className="btn btn-danger btn-sm"
              >
                Delete
              </button>
            </>
          )}
          <Link to="/issues" className="btn btn-outline btn-sm">
            Back to Issues
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2">
          {/* Issue Description */}
          <div className="card mb-6">
            <div className="card-header">
              <h3 className="card-title">Description</h3>
            </div>
            <div className="card-body">
              <div className="whitespace-pre-wrap">{issue.description}</div>
            </div>
          </div>

          {/* Comments */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Comments ({comments.length})</h3>
            </div>
            <div className="card-body">
              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="mb-6">
                <div className="form-group">
                  <label className="form-label">Add a comment</label>
                  <textarea
                    className="form-control textarea"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write your comment here..."
                    rows={3}
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-sm">
                  Add Comment
                </button>
              </form>

              {/* Comments List */}
              <div className="space-y-4">
                {comments.map(comment => (
                  <div key={comment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <div className="font-medium">{comment.author_name}</div>
                      <div className="text-sm text-gray-600">
                        {formatDate(comment.created_at)}
                      </div>
                    </div>
                    <div className="whitespace-pre-wrap text-gray-800">
                      {comment.content}
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    No comments yet. Be the first to comment!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          {/* Quick Actions */}
          <div className="card mb-6">
            <div className="card-header">
              <h3 className="card-title">Quick Actions</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                <div>
                  <label className="form-label">Status</label>
                  <select
                    className="form-control form-select"
                    value={issue.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>

                {users.length > 0 && (
                  <div>
                    <label className="form-label">Assigned To</label>
                    <select
                      className="form-control form-select"
                      value={issue.assigned_to || ''}
                      onChange={(e) => handleAssignment(e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Issue Details */}
          <div className="card mb-6">
            <div className="card-header">
              <h3 className="card-title">Details</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Priority:</span>
                  <span className={`badge ${getPriorityBadgeClass(issue.priority)}`}>
                    {issue.priority}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Status:</span>
                  <span className={`badge ${getStatusBadgeClass(issue.status)}`}>
                    {issue.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Assigned to:</span>
                  <span>{issue.assignee_name || 'Unassigned'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Creator:</span>
                  <span>{issue.creator_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Created:</span>
                  <span>{formatDate(issue.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Updated:</span>
                  <span>{formatDate(issue.updated_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Activity</h3>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {activities.slice(0, 5).map(activity => (
                  <div key={activity.id} className="text-sm">
                    <div className="font-medium">{activity.user_name}</div>
                    <div className="text-gray-600">{activity.description}</div>
                    <div className="text-xs text-gray-500">
                      {formatDate(activity.created_at)}
                    </div>
                  </div>
                ))}
                {activities.length === 0 && (
                  <div className="text-center text-gray-500 text-sm">
                    No activity yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IssueDetail; 